import PropTypes from 'prop-types';
import { useContext } from 'react';
import { Checkbox, Tag } from 'antd';
import condStrings from '../../../utils/condStrings';
import DataRowContext from '../DataRowContext';
import CheckableTagList from '../../checkableTagList';
import isValidHttpUrl from '../../../utils/isValidHttpUrl';
import StaticDataPhotos from './StaticDataPhotos';
import { CONTACT_MERGE_FIELDS, CONTACT_FIELD_LABELS } from '../dataRowUtils';

export default function StaticData(props) {
  const { side, content, extra, noValues, showIncomplete } = props;
  const {
    isConflict,
    type,
    options,
    readOnly,
    checkedSide,
    onItemSelect,
    selectedItems,
    setCheckedSide,
  } = useContext(DataRowContext);

  // Read-only list, used for the automatic submitter row (content is an array).
  const renderContactsList = () =>
    content.map((contact, idx) => (
      <div className="row-contacts-item" key={`${contact?.id}${idx}`}>
        <li>
          <span className="label">Name:</span>
          <span className="value">{contact?.name}</span>
        </li>
        <li>
          <span className="label">Email:</span>
          <span className="value">{contact?.email}</span>
        </li>
        <li>
          <span className="label">Phone:</span>
          <span className="value">{contact?.phone}</span>
        </li>
        {contact?.is_primary && (
          <li>
            <span className="label">
              <b>Primary Contact</b>
            </span>
          </li>
        )}
      </div>
    ));

  // A single contact box with a per-field checkbox to pull that field's value
  // into the center, mirroring the per-photo selection in StaticDataPhotos.
  const renderContactMerge = () => {
    const displayValue = (field) => {
      if (field === 'contact_type_id') {
        return (
          options.find((opt) => opt.value === content.contact_type_id)?.label ||
          content.contact_type_id
        );
      }
      if (field === 'is_primary') return content[field] ? 'True' : 'False';
      return content[field];
    };

    return (
      <div className="contacts">
        {CONTACT_MERGE_FIELDS.map((field) => (
          <div className="data-row-field" key={field}>
            <Checkbox
              disabled={readOnly}
              checked={selectedItems?.values?.[field] === content[field]}
              onChange={() => onItemSelect(side)({ target: { value: field } })}
            />
            <span>{CONTACT_FIELD_LABELS[field]}:</span>
            <div>{displayValue(field)}</div>
          </div>
        ))}
        {showIncomplete && (
          <Tag color="warning" className="contact-incomplete-tag">
            Less complete
          </Tag>
        )}
      </div>
    );
  };

  const generateContent = () => {
    if (type === 'contacts') {
      if (Array.isArray(content)) return renderContactsList();
      return content ? renderContactMerge() : null;
    }

    if (type === 'tags') {
      return <CheckableTagList options={options} value={content} readOnly />;
    }

    if (type === 'tags_input') {
      return content.map((tag, idx) => (
        <Tag key={`${side}-${tag.id || idx}`} className="mb-1">
          {tag.name}
        </Tag>
      ));
    }

    if (type === 'url') {
      if (isValidHttpUrl(content)) {
        return (
          <a
            href={content}
            target="_blank"
            rel="noopener noreferrer"
            style={{ wordBreak: 'break-word' }}
          >
            {content}
          </a>
        );
      }
    }

    if (type === 'select') {
      return content?.label;
    }

    if (type === 'photos') {
      return (
        <StaticDataPhotos
          side={side}
          setCheckedSide={setCheckedSide}
          checkedSide={checkedSide}
          content={content}
          selected={selectedItems}
          onSelect={onItemSelect}
        />
      );
    }

    if (type === 'bool') {
      // bool content was changed false to true and true to false
      // because: Landing Possible --> Landing NOT Possible
      return content ? 'False' : 'True';
    }

    if (type === 'survey') {
      return content?.survey_progress_percentage
        ? `Survey progress: ${content.survey_progress_percentage}%`
        : null;
    }

    return content;
  };

  return (
    <div
      className={condStrings(
        'data-row-info',
        side,
        isConflict && 'is-conflict',
        noValues && 'invisible',
      )}
    >
      <div className="data-row-content">
        {generateContent()}
        <p className="mb-0">{extra}</p>
      </div>
    </div>
  );
}

StaticData.propTypes = {
  side: PropTypes.oneOf(['next', 'prev']),
  content: PropTypes.any,
  extra: PropTypes.any,
  noValues: PropTypes.bool,
  showIncomplete: PropTypes.bool,
};

StaticData.defaultProps = {
  side: 'prev',
  content: '',
  extra: null,
  noValues: false,
  showIncomplete: false,
};
